import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

class QuickViewDialog extends DialogComponent {
  #abortController = new AbortController();
  #savedScrollY = undefined;
  // Capture parent's closeDialog arrow field before our override replaces it.
  // Field initializers run in declaration order, so this must come before closeDialog.
  #parentCloseDialog = this.closeDialog;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Handles cart update events. Closes QV dialog on successful cart adds only.
   * AddToCartInterceptor then opens the ATC confirmation modal.
   * Guards against closing on errors (e.g., max quantity exceeded).
   * @param {Event} event - CartAddEvent with detail.data shape
   */
  #onCartUpdate = (event) => {
    const data = event.detail?.data;
    // Only close on successful adds (not errors like max quantity exceeded)
    if (data?.didError) return;
    // Ensure this is a cart add event with a source, not a generic cart refresh
    if (!data?.source) return;
    this.closeDialog();
  };

  /**
   * Saves scroll position before showing dialog.
   * Belt-and-suspenders for scroll preservation (FLOW-03).
   */
  showDialog() {
    this.#savedScrollY = window.scrollY;
    super.showDialog();
  }

  /**
   * Restores scroll position after dialog close animation completes.
   * Restores window.scrollY saved in showDialog().
   */
  closeDialog = async () => {
    await this.#parentCloseDialog();
    if (this.#savedScrollY !== undefined) {
      window.scrollTo({ top: this.#savedScrollY, behavior: 'instant' });
      this.#savedScrollY = undefined;
    }
  };
}

class QuickViewButtonComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Document | null} */
  #cachedHtml = null;

  /**
   * Resolves the product page URL from the closest product-card link.
   * Mirrors QuickAddComponent.productPageUrl getter.
   * @returns {string | undefined}
   */
  get productPageUrl() {
    return (
      this.dataset.productUrl ||
      this.closest('product-card')?.querySelector('a[ref="productCardLink"]')?.href
    );
  }

  /**
   * Builds the quick-view URL using Shopify's product route (with locale/market prefix
   * preserved when present). The ?view= alternate template only works on Shopify product
   * routes, not on Constructor.io SSR paths like /collections/.../p/{handle} or /p/{handle}
   * nested under collection/search — those are rewritten to .../products/{handle}.
   * @returns {string | null}
   */
  get quickViewUrl() {
    const url = this.productPageUrl;
    if (!url) return null;

    const parsed = new URL(url, window.location.origin);
    let pathname = parsed.pathname;
    if (pathname.length > 1) pathname = pathname.replace(/\/+$/, '');

    const params = new URLSearchParams(parsed.search);
    params.set('view', 'quick-view');
    const query = params.toString();

    const productsMarker = '/products/';
    const productsIdx = pathname.indexOf(productsMarker);
    if (productsIdx !== -1) {
      const afterProducts = pathname.slice(productsIdx + productsMarker.length);
      const handle = afterProducts.split('/').filter(Boolean)[0];
      if (!handle) return null;
      const productPath = pathname.slice(0, productsIdx + productsMarker.length) + handle;
      return `${productPath}?${query}`;
    }

    let match = pathname.match(/^\/p\/([^/]+)$/);
    if (match) {
      return `/products/${match[1]}?${query}`;
    }

    match = pathname.match(/^(.+)\/p\/([^/]+)$/);
    if (match) {
      const [, beforeP = '', handle = ''] = match;
      if (!handle) return null;
      let marketPrefix = '';
      const collectionsIdx = beforeP.indexOf('/collections/');
      const searchIdx = beforeP.indexOf('/search');
      if (collectionsIdx !== -1) {
        marketPrefix = beforeP.slice(0, collectionsIdx);
      } else if (searchIdx !== -1) {
        marketPrefix = beforeP.slice(0, searchIdx);
      } else if (/^\/[^/]+$/.test(beforeP)) {
        // Legacy /p/ URL with a single leading segment only, e.g. /en-ca/p/foo from
        // product.url with use_legacy_product_urls (Liquid replaces /products/ -> /p/).
        marketPrefix = beforeP;
      }
      return `${marketPrefix}/products/${handle}?${query}`;
    }

    return null;
  }

  /**
   * Click handler bound via on:click="quick-view-button-component/handleClick".
   * D-06: Opens modal immediately with spinner, then fetches and morphs content.
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const dialog = /** @type {QuickViewDialog} */ (document.getElementById('quick-view-dialog'));
    if (!(dialog instanceof QuickViewDialog)) return;

    const modalContent = document.getElementById('quick-view-modal-content');
    if (!modalContent) return;

    // D-06: Inject spinner BEFORE opening dialog (prevents empty flash)
    this.#showSpinner(modalContent);
    dialog.showDialog();

    // D-05: Use cached HTML if available
    if (!this.#cachedHtml) {
      await this.#fetchProductPage(this.quickViewUrl);
    }

    if (this.#cachedHtml) {
      // Clone cached document to prevent morph from mutating the cache
      // (same pattern as QuickAddComponent.handleClick)
      const freshCopy = new DOMParser().parseFromString(
        this.#cachedHtml.documentElement.outerHTML,
        'text/html'
      );
      try {
        morph(modalContent, freshCopy.body);
      } catch (error) {
        console.error('[quick-view] morph error', error);
      }
    } else {
      // Fetch failed — close the dialog (matches quick-add error behavior)
      dialog.closeDialog();
    }
  };

  /**
   * Injects a centered loading spinner into the modal content container.
   * Markup matches frontend/components/Spinner.tsx pattern per UI-SPEC.
   * @param {HTMLElement} container
   */
  #showSpinner(container) {
    container.innerHTML = `
      <div class="quick-view-spinner" role="status" aria-label="Loading..." style="display:flex;align-items:center;justify-content:center;min-height:200px;">
        <div class="spinner animate-spin rounded-full border-4 border-t-transparent border-primary-button-border h-10 w-10"></div>
        <span class="visually-hidden">Loading...</span>
      </div>
    `;
  }

  /**
   * Fetches the quick-view HTML for a product.
   * Mirrors QuickAddComponent.fetchProductPage with AbortController.
   * @param {string | null} url
   */
  async #fetchProductPage(url) {
    if (!url) return;

    // Abort any in-flight fetch before starting a new one
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const fetchController = this.#abortController;

    try {
      const response = await fetch(url, { signal: fetchController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      // Ignore stale body if a newer fetch replaced our controller before we parsed
      if (this.#abortController !== fetchController) return;
      this.#cachedHtml = new DOMParser().parseFromString(html, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[quick-view] fetch failed', error);
    } finally {
      // Only clear if we still own the slot — an older invocation's finally must not
      // null a newer call's controller (would break abort and allow fetch races).
      if (this.#abortController === fetchController) {
        this.#abortController = null;
      }
    }
  }
}

/**
 * QuickViewGallery — controls the gallery inside the quick view modal.
 * - Thumbnail click swaps the main image and toggles the active state.
 * - Main image prev/next buttons step through media cyclically.
 * - Thumbnail strip prev/next buttons horizontally scroll the thumb row.
 * Wired via the declarative event pattern, e.g. `on:click="quick-view-gallery/handleThumbClick"`.
 */
class QuickViewGallery extends Component {
  /** @type {ResizeObserver | null} */
  #thumbsResizeObserver = null;
  /** @type {MutationObserver | null} */
  #thumbsMutationObserver = null;
  /** @type {MutationObserver | null} */
  #rootMutationObserver = null;
  /** @type {HTMLElement | null} */
  #observedTrack = null;

  connectedCallback() {
    super.connectedCallback();
    // Observe our own subtree so we catch late-arriving thumbs when morph() inserts
    // content after the custom element is already connected.
    this.#rootMutationObserver = new MutationObserver(() => this.#refreshAll());
    this.#rootMutationObserver.observe(this, { childList: true, subtree: true });
    this.#attachTrackListeners();
    // Run refresh across multiple frames/timings so we catch whatever the actual
    // layout-ready moment is (requestAnimationFrame fires before paint; the 100ms
    // fallback handles dialogs that are still animating in).
    requestAnimationFrame(() => this.#refreshAll());
    setTimeout(() => this.#refreshAll(), 100);
    setTimeout(() => this.#refreshAll(), 400);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#rootMutationObserver?.disconnect();
    this.#rootMutationObserver = null;
    this.#thumbsResizeObserver?.disconnect();
    this.#thumbsResizeObserver = null;
    this.#thumbsMutationObserver?.disconnect();
    this.#thumbsMutationObserver = null;
    this.#observedTrack?.removeEventListener('scroll', this.#onThumbsScroll);
    this.#observedTrack?.querySelectorAll('img').forEach((img) => {
      img.removeEventListener('load', this.#onThumbImgLoad);
    });
    this.#observedTrack = null;
  }

  /** Idempotently attach observers/listeners to the current track element. */
  #attachTrackListeners() {
    const track = this.#track;
    if (!track || track === this.#observedTrack) return;
    // Teardown any prior track listeners before re-attaching.
    this.#thumbsResizeObserver?.disconnect();
    this.#thumbsMutationObserver?.disconnect();
    this.#observedTrack?.removeEventListener('scroll', this.#onThumbsScroll);
    if ('ResizeObserver' in window) {
      this.#thumbsResizeObserver = new ResizeObserver(() => this.#refreshThumbsState());
      this.#thumbsResizeObserver.observe(track);
    }
    this.#thumbsMutationObserver = new MutationObserver(() => this.#refreshThumbsState());
    this.#thumbsMutationObserver.observe(track, { childList: true });
    track.addEventListener('scroll', this.#onThumbsScroll, { passive: true });
    track.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', this.#onThumbImgLoad);
    });
    this.#observedTrack = track;
  }

  /** Runs the complete state refresh: re-attach listeners, overflow, and edges. */
  #refreshAll() {
    this.#attachTrackListeners();
    this.#refreshThumbsState();
    this.#refreshMainEdgeDisabled();
  }

  #onThumbImgLoad = () => this.#refreshThumbsState();
  #onThumbsScroll = () => this.#refreshEdgeDisabled();

  /** @param {Event} event */
  handleThumbClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const thumb = target?.closest?.('.quick-view-media__thumb');
    if (thumb) this.#activateThumb(thumb);
  };

  handleMainPrev = () => this.#step(-1);
  handleMainNext = () => this.#step(1);

  handleThumbsPrev = () => this.#scrollThumbs(-1);
  handleThumbsNext = () => this.#scrollThumbs(1);

  get #track() {
    return /** @type {HTMLElement | null} */ (
      this.refs?.thumbsTrack || this.querySelector('.quick-view-media__thumbs')
    );
  }

  /** Toggles overflow class and edge-disabled state together. */
  #refreshThumbsState() {
    const track = this.#track;
    const wrap = this.querySelector('.quick-view-media__thumbs-wrap');
    if (!track || !wrap) return;
    const hasOverflow = track.scrollWidth - track.clientWidth > 1;
    wrap.classList.toggle('quick-view-media__thumbs-wrap--has-overflow', hasOverflow);
    this.#refreshEdgeDisabled();
  }

  /**
   * Toggles the `disabled` attribute on the thumb prev/next buttons based on
   * the current scroll position. Matches the carousel convention in media-components.css.
   */
  #refreshEdgeDisabled() {
    const track = this.#track;
    if (!track) return;
    const prev = this.querySelector('.quick-view-media__thumbs-nav--prev');
    const next = this.querySelector('.quick-view-media__thumbs-nav--next');
    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= maxScroll - 1;
    if (prev) prev.toggleAttribute('disabled', atStart);
    if (next) next.toggleAttribute('disabled', atEnd);
  }

  /**
   * Swaps the main image to the given thumbnail and updates active state.
   * @param {Element} thumb
   */
  #activateThumb(thumb) {
    const newSrc = /** @type {HTMLElement} */ (thumb).dataset.largeSrc;
    const mainImg = /** @type {HTMLImageElement | null} */ (
      this.refs?.mainImage || this.querySelector('.quick-view-media__main-image')
    );
    if (!mainImg || !newSrc) return;
    // Swap the main image. Clear srcset so the browser uses the large src directly.
    mainImg.removeAttribute('srcset');
    mainImg.src = newSrc;
    this.querySelectorAll('.quick-view-media__thumb').forEach((b) => {
      b.classList.toggle('is-active', b === thumb);
    });
    // Keep the active thumbnail visible in the strip.
    /** @type {HTMLElement} */ (thumb).scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
    this.#refreshMainEdgeDisabled();
  }

  /** @param {number} direction -1 for prev, 1 for next */
  #step(direction) {
    const thumbs = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('.quick-view-media__thumb'),
    ]);
    if (thumbs.length <= 1) return;
    const activeIdx = thumbs.findIndex((t) => t.classList.contains('is-active'));
    const current = activeIdx === -1 ? 0 : activeIdx;
    const target = current + direction;
    if (target < 0 || target >= thumbs.length) return;
    this.#activateThumb(thumbs[target]);
  }

  /**
   * Toggles the `disabled` attribute on the main image prev/next buttons based
   * on the position of the active thumbnail. Prev is disabled on the first image,
   * next is disabled on the last.
   */
  #refreshMainEdgeDisabled() {
    const thumbs = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('.quick-view-media__thumb'),
    ]);
    const prev = this.querySelector('.quick-view-media__nav--prev');
    const next = this.querySelector('.quick-view-media__nav--next');
    if (!prev && !next) return;
    if (thumbs.length <= 1) {
      prev?.toggleAttribute('disabled', true);
      next?.toggleAttribute('disabled', true);
      return;
    }
    const activeIdx = thumbs.findIndex((t) => t.classList.contains('is-active'));
    const current = activeIdx === -1 ? 0 : activeIdx;
    prev?.toggleAttribute('disabled', current <= 0);
    next?.toggleAttribute('disabled', current >= thumbs.length - 1);
  }

  /** @param {number} direction -1 for left, 1 for right */
  #scrollThumbs(direction) {
    const track = this.#track;
    if (!track) return;
    // Scroll by two thumbnails-worth (2 × 100 + 2 × 20). CSS `scroll-behavior: smooth` on the
    // track handles animation; plain scrollLeft assignment is more reliable across environments.
    track.scrollLeft += direction * 240;
    // Scroll events handle the common case; refresh once after animation likely
    // settles in case events are throttled or suppressed.
    setTimeout(() => this.#refreshEdgeDisabled(), 300);
  }
}

if (!customElements.get('quick-view-dialog')) {
  customElements.define('quick-view-dialog', QuickViewDialog);
}

if (!customElements.get('quick-view-button-component')) {
  customElements.define('quick-view-button-component', QuickViewButtonComponent);
}

if (!customElements.get('quick-view-gallery')) {
  customElements.define('quick-view-gallery', QuickViewGallery);
}
